import { Children, type ReactNode } from "react";

import { cn } from "@/lib/utils";

/**
 * Skjemaets byggeklosser. Se reglene i Page.tsx.
 *
 * - <FormSections> legger seksjonene i én kolonne, eller to på brede skjermer
 *   (`columns={2}`): første halvdel til venstre, resten til høyre. DOM-,
 *   fokus- og leserekkefølgen er den samme som den visuelle.
 * - <FormSection> er én gruppe felt for én oppgave, med tittel og valgfri
 *   hjelpetekst.
 * - <FormRow> setter korte, relaterte felt på samme rad (postnr + poststed,
 *   innkjøp + utsalg). På mobil stables de.
 * - Feltbredde settes på selve feltet med max-w-field-xs/sm/md. Fritekst og
 *   nedtrekk med lange verdier får hele seksjonsbredden.
 * - <FormActions> er handlingslinja nederst: sekundære handlinger (Slett) til
 *   venstre, Avbryt og hovedhandlingen til høyre, hovedhandlingen ytterst.
 *   Den klistrer seg til bunnen av hovedfeltet når skjemaet er langt.
 */

export const FormSections = ({
  columns = 1,
  className,
  children,
}: {
  columns?: 1 | 2;
  className?: string;
  children: ReactNode;
}) => {
  if (columns === 1) {
    return (
      <div className={cn("flex flex-col gap-8", className)}>{children}</div>
    );
  }
  // To uavhengige kolonner, ikke et grid: i et grid blir hver rad like høy
  // som den høyeste seksjonen, og en kort seksjon får et hull under seg.
  // Første halvdel av seksjonene står i venstre kolonne, resten i høyre.
  // DOM-, fokus- og leserekkefølgen følger kolonnene, og på mobil stables
  // de i samme rekkefølge.
  const items = Children.toArray(children);
  const split = Math.ceil(items.length / 2);
  return (
    <div
      className={cn(
        "flex flex-col gap-8 lg:flex-row lg:items-start lg:gap-12",
        className,
      )}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-8">
        {items.slice(0, split)}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-8">
        {items.slice(split)}
      </div>
    </div>
  );
};

export const FormSection = ({
  title,
  description,
  className,
  children,
}: {
  title?: ReactNode;
  description?: ReactNode;
  className?: string;
  children: ReactNode;
}) => (
  <section className={cn("flex min-w-0 flex-col gap-4", className)}>
    {(title || description) && (
      <div className="flex flex-col gap-1">
        {title && <h2 className="section-title">{title}</h2>}
        {description && (
          <p className="text-sm text-muted-foreground">{description}</p>
        )}
      </div>
    )}
    {children}
  </section>
);

export const FormRow = ({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) => (
  <div
    className={cn(
      "flex flex-col gap-4 sm:flex-row sm:items-start [&>*]:min-w-0",
      className,
    )}
  >
    {children}
  </div>
);

/**
 * Handlingslinja i bunnen av et skjemakort. Forutsetter at den står inne i
 * <CardContent> (px-6) og at hovedfeltet har --page-gutter (Layout.tsx).
 */
export const FormActions = ({
  start,
  className,
  children,
}: {
  /** Sekundære/destruktive handlinger til venstre, f.eks. Slett. */
  start?: ReactNode;
  className?: string;
  /** Avbryt og hovedhandlingen, hovedhandlingen sist. */
  children: ReactNode;
}) => (
  <div
    role="toolbar"
    className={cn(
      "sticky bottom-[calc(-1*var(--page-gutter,1rem))] z-10 -mx-6 mt-6 flex flex-row flex-wrap items-center gap-2 border-t bg-card px-6 py-3",
      className,
    )}
  >
    {start && <div className="flex flex-row items-center gap-2">{start}</div>}
    <div className="ml-auto flex flex-row flex-wrap items-center justify-end gap-2">
      {children}
    </div>
  </div>
);
